import {
  Button,
  Chip,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import { ChangeEvent, FormEvent, FunctionComponent, useState } from "react";
import { Color } from "../../colors";
import { stylesheet } from "../../stylesheet";
import { theme } from "../../WebClientContexts";
import { AttachmentDialogTestids } from "../AttachmentDialog";

/**
 * Enum for the kinds of filters available in the filter bar
 */
export enum FilterKind {
  Name = "name",
  Extension = "extension",
}

/**
 * Type for currently applied filters. Filters that are not active
 * will have a value of empty string ("").
 */
export type ActiveFilters = Record<FilterKind, string>;

export interface FilterBarProps {
  /**
   * Invoked when a filter is added or removed
   * @param filters The new active filters
   */
  onFilterChange: (filters: ActiveFilters) => void;
  /**
   * Currently active filters, controls chips.
   */
  activeFilters: ActiveFilters;
}

/**
 * Type guard for narrowing strings to {@link FilterKind}s
 * @param value
 * @returns true if the provided value is a `FilterKind`
 */
const isFilterKind = (value: unknown): value is FilterKind => {
  return value === FilterKind.Name || value === FilterKind.Extension;
};

/**
 * Generate a user-friendly display name for a {@link FilterKind}
 * @param filterKind
 * @returns A human-readable name for the specified filter kind appropriate
 *  for use in the UI.
 */
const filterKindToString = (filterKind: FilterKind) => {
  return {
    [FilterKind.Extension]: "File Type",
    [FilterKind.Name]: "Name",
  }[filterKind];
};

/**
 * Component to allow a user to filter the `AttachmentDialog` document list.
 * Simplified version of what's available in listview.
 */
export const FilterBar: FunctionComponent<FilterBarProps> = ({
  onFilterChange,
  activeFilters,
}) => {
  /**
   * Currently selected filter type from the filter type dropdown
   */
  const [selectedFilterType, setSelectedFilterType] = useState<FilterKind>(
    FilterKind.Name
  );
  /**
   * Current filter value (either from the extension dropdown or name input)
   */
  const [selectedFilterValue, setSelectedFilterValue] = useState<string>("");

  /**
   * Event handler for changes to the filter type dropdown.
   * @param event
   */
  const handleFilterTypeChange = (event: SelectChangeEvent<FilterKind>) => {
    if (isFilterKind(event.target.value)) {
      setSelectedFilterType(event.target.value);
      setSelectedFilterValue("");
    }
  };

  /**
   * Event handler for changes to the filter value input
   * @param event
   */
  const handleFilterValueChange = (
    event:
      | SelectChangeEvent<string>
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSelectedFilterValue(event.target.value);
  };

  /**
   * Event handler for clicking the `Filter` button
   * @param event
   */
  const handleFilterApply = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const newActiveFilters = {
      ...activeFilters,
      [selectedFilterType]: selectedFilterValue,
    };
    onFilterChange(newActiveFilters);
    setSelectedFilterValue("");
  };

  /**
   * Event handler for clicks on filter chip remove buttons
   * @param key The filter kind to remove
   */
  const handleFilterRemove = (key: FilterKind) => {
    const newActiveFilters = { ...activeFilters };
    newActiveFilters[key] = "";
    onFilterChange(newActiveFilters);
  };

  /**
   * Event handler for clicks on the `Clear All` button
   */
  const handleClearAll = () => {
    const newActiveFilters = { ...activeFilters };
    for (const key in newActiveFilters) {
      newActiveFilters[key as unknown as FilterKind] = "";
    }
    onFilterChange(newActiveFilters);
  };

  const filterInput = {
    [FilterKind.Extension]: (
      <Select
        inputProps={{
          "data-testid": AttachmentDialogTestids.FilterInput,
        }}
        label=""
        onChange={handleFilterValueChange}
        value={selectedFilterValue}
        css={ss.filterInput}
        size="small"
      >
        {supportedExtensions.map((extension) => (
          <MenuItem key={extension} value={extension}>
            {extension}
          </MenuItem>
        ))}
      </Select>
    ),
    [FilterKind.Name]: (
      <TextField
        inputProps={{
          "data-testid": AttachmentDialogTestids.FilterInput,
        }}
        variant="outlined"
        onChange={handleFilterValueChange}
        value={selectedFilterValue}
        css={ss.filterInput}
        size="small"
      />
    ),
  }[selectedFilterType];

  return (
    <div css={ss.root}>
      <form css={ss.form} onSubmit={handleFilterApply}>
        <InputLabel id="select-label" css={ss.sectionLabel}>
          Filter by
        </InputLabel>
        <Stack direction="row">
          <Select
            inputProps={{
              "data-testid": AttachmentDialogTestids.FilterSelect,
            }}
            labelId="select-label"
            onChange={handleFilterTypeChange}
            value={selectedFilterType}
            css={ss.filterTypeSelect}
            size="small"
          >
            <MenuItem value={FilterKind.Name}>Name</MenuItem>
            <MenuItem value={FilterKind.Extension}>File Type</MenuItem>
          </Select>
          {filterInput}
          <Button
            data-testid={AttachmentDialogTestids.FilterButton}
            disabled={selectedFilterValue === ""}
            variant="contained"
            type="submit"
          >
            FILTER
          </Button>
        </Stack>
      </form>
      <Stack direction="row" marginTop={theme.spacing(1)}>
        {Object.keys(activeFilters).map((key) => {
          if (activeFilters[key as FilterKind] === "") return "";
          else
            return (
              <Chip
                key={key}
                label={`${filterKindToString(key as FilterKind)}: ${
                  activeFilters[key as FilterKind]
                }`}
                onDelete={() => handleFilterRemove(key as FilterKind)}
                data-testid={AttachmentDialogTestids.FilterChip(
                  key as FilterKind
                )}
                variant="outlined"
                css={ss.chip}
              />
            );
        })}
        {Object.values(activeFilters).some((v) => v) && (
          <Button
            variant="text"
            onClick={handleClearAll}
            data-testid={AttachmentDialogTestids.FilterClearAllButton}
          >
            Clear All
          </Button>
        )}
      </Stack>
    </div>
  );
};

const ss = stylesheet({
  root: (theme) => ({
    padding: theme.spacing(2),
    backgroundColor: Color.LightGray,
    display: "flex",
    flexDirection: "column",
  }),
  /**
   * Styles for the `Filter By` label
   */
  sectionLabel: (theme) => ({
    marginLeft: theme.spacing(1),
    fontSize: "12px",
    fontWeight: "bold",
  }),
  /**
   * Styles for the filter form itself
   */
  form: {
    flexGrow: 1,
  },
  /**
   * Styles for the filter type dropdown
   */
  filterTypeSelect: { backgroundColor: Color.White, width: "200px" },
  /**
   * Styles for the filter input
   */
  filterInput: (theme) => ({
    backgroundColor: Color.White,
    flexGrow: 1,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  }),
  /**
   * Styles for the filter chips
   */
  chip: { backgroundColor: Color.White },
});

/**
 * Extensions supported for the extension filter dropdown
 */
const supportedExtensions = [
  "doc",
  "docx",
  "dwfx",
  "jpg",
  "jpeg",
  "mp3",
  "mp4",
  "pdf",
  "png",
  "rvt",
  "xls",
  "xlsx",
  "zip",
];
